import { useClerk } from "@clerk/react"
import { useQueryClient } from "@tanstack/react-query"
import { useNavigate } from "@tanstack/react-router"
import { ChevronRight, History } from "lucide-react"
import * as React from "react"
import { toast } from "sonner"
import { useTheme } from "@/components/global/theme/theme-provider"
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
	CommandLoading,
	CommandSeparator,
	CommandShortcut,
} from "@/components/ui/command"
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog"
import { useSidebar } from "@/components/ui/sidebar"
import { useAdminRolesContext } from "@/contexts/admin-roles-context"
import { useMarketplaceOrpc, useOrpc } from "@/integrations/orpc"
import { buildCreateCommands } from "./commands/create-commands"
import { buildNavigationCommands } from "./commands/navigation"
import { buildPresetCommands } from "./commands/presets"
import { buildRunCommands } from "./commands/run-commands"
import { buildSwitchCommands } from "./commands/switch-commands"
import {
	catalogueHitToCommand,
	hitPrecedence,
	isExactCatalogueHit,
	isExactMarketplaceHit,
	marketplaceHitToCommand,
	stripeOtherCommand,
} from "./entity"
import { rankCommands } from "./ranking"
import {
	loadRecents,
	pushRecent,
	type RecentEntry,
	type RecentsStore,
} from "./recents"
import {
	filterByRoles,
	PALETTE_GROUP_LABELS,
	type PaletteCommand,
	type PaletteContext,
	type PaletteGroup,
	type PaletteStep,
} from "./types"
import { useEntityLookup } from "./use-entity-lookup"

const KIND_LABELS: Record<string, string> = {
	hexId: "internal id",
	orderNumber: "order number",
	transactionNumber: "transaction number",
	clerkUserId: "Clerk user id",
	stripeAccount: "Stripe account",
	stripeCustomer: "Stripe customer",
	stripeCheckoutSession: "Stripe checkout session",
	stripePaymentIntent: "Stripe payment intent",
	stripeCharge: "Stripe charge",
	stripeOther: "Stripe id",
	email: "email",
	ip: "IP address",
	cidr: "CIDR",
	code: "code",
	numeric: "number",
}

function browserStorage(): RecentsStore {
	return {
		getItem: (key) => window.localStorage.getItem(key),
		setItem: (key, value) => {
			window.localStorage.setItem(key, value)
		},
	}
}

export function CommandPalette({
	open,
	onOpenChange,
	contextualCommands,
}: {
	open: boolean
	onOpenChange: (open: boolean) => void
	contextualCommands: PaletteCommand[]
}) {
	const navigate = useNavigate()
	const queryClient = useQueryClient()
	const { roles, hasAnyRole, isSuperadmin } = useAdminRolesContext()
	const { client: marketplaceClient } = useMarketplaceOrpc()
	const { client: gameAdminClient } = useOrpc()
	const { setTheme } = useTheme()
	const { toggleSidebar } = useSidebar()
	const { signOut, openUserProfile } = useClerk()

	const [query, setQuery] = React.useState("")
	const [steps, setSteps] = React.useState<PaletteStep[]>([])
	const [value, setValue] = React.useState("")
	const [stepLoading, setStepLoading] = React.useState(false)
	const [recents, setRecents] = React.useState<RecentEntry[]>([])

	React.useEffect(() => {
		if (open) {
			setQuery("")
			setSteps([])
			setValue("")
			try {
				setRecents(loadRecents(browserStorage()))
			} catch {
				setRecents([])
			}
		}
	}, [open])

	const close = React.useCallback(() => onOpenChange(false), [onOpenChange])

	const push = React.useCallback((step: PaletteStep) => {
		setSteps((current) => [...current, step])
		setQuery("")
	}, [])

	const pop = React.useCallback(() => {
		setSteps((current) => current.slice(0, -1))
		setQuery("")
	}, [])

	const pushAsync = React.useCallback(
		(title: string, load: () => Promise<PaletteCommand[]>) => {
			setStepLoading(true)
			setSteps((current) => [...current, { title, commands: [] }])
			setQuery("")
			load()
				.then((commands) => {
					setSteps((current) => {
						const top = current[current.length - 1]
						if (!top || top.title !== title) return current
						return [...current.slice(0, -1), { ...top, commands }]
					})
				})
				.catch(() => {
					toast.error("Failed to load")
					setSteps((current) => current.slice(0, -1))
				})
				.finally(() => setStepLoading(false))
		},
		[],
	)

	const ctx = React.useMemo<PaletteContext>(
		() => ({
			navigate,
			queryClient,
			roles,
			hasAnyRole,
			isSuperadmin,
			marketplaceClient,
			gameAdminClient,
			close,
			push,
			pushAsync,
		}),
		[
			navigate,
			queryClient,
			roles,
			hasAnyRole,
			isSuperadmin,
			marketplaceClient,
			gameAdminClient,
			close,
			push,
			pushAsync,
		],
	)

	const navigationCommands = React.useMemo(buildNavigationCommands, [])
	const presetCommands = React.useMemo(buildPresetCommands, [])
	const createCommands = React.useMemo(buildCreateCommands, [])
	const runCommands = React.useMemo(buildRunCommands, [])
	const switchCommands = React.useMemo(
		() =>
			buildSwitchCommands({
				setTheme,
				toggleSidebar,
				signOut: () => signOut({ redirectUrl: "/" }),
				openUserProfile: () => openUserProfile(),
			}),
		[setTheme, toggleSidebar, signOut, openUserProfile],
	)

	const staticCommands = React.useMemo(
		() => [
			...navigationCommands,
			...presetCommands,
			...createCommands,
			...runCommands,
			...switchCommands,
		],
		[
			navigationCommands,
			presetCommands,
			createCommands,
			runCommands,
			switchCommands,
		],
	)

	const commandScoped = query.startsWith(">")
	const effectiveQuery = commandScoped ? query.slice(1).trim() : query.trim()
	const atRoot = steps.length === 0

	const lookup = useEntityLookup(atRoot && !commandScoped ? query : "")

	const entityCommands = React.useMemo(() => {
		if (!atRoot || commandScoped) return { exact: [], partial: [] }
		if (
			lookup.classified.kind === "stripeOther" &&
			lookup.classified.value.length > 0
		) {
			return {
				exact: [stripeOtherCommand(lookup.classified.value)],
				partial: [],
			}
		}
		const scored = [
			...lookup.marketplaceHits.flatMap((hit) => {
				const command: PaletteCommand | undefined = marketplaceHitToCommand(hit)
				if (!command) return []
				return [
					{
						command,
						exact: isExactMarketplaceHit(hit, lookup.classified.value),
						precedence: hitPrecedence(hit.type),
					},
				]
			}),
			...lookup.catalogueHits.flatMap((hit) => {
				const command: PaletteCommand | undefined = catalogueHitToCommand(hit)
				if (!command) return []
				return [
					{
						command,
						exact: isExactCatalogueHit(hit),
						precedence: hitPrecedence(hit.type),
					},
				]
			}),
		]
		const allowed = new Set(
			filterByRoles(
				scored.map((entry) => entry.command),
				hasAnyRole,
			).map((command) => command.id),
		)
		const visible = scored
			.filter((entry) => allowed.has(entry.command.id))
			.sort((a, b) => a.precedence - b.precedence)

		return {
			exact: visible
				.filter((entry) => entry.exact)
				.map((entry) => entry.command),
			partial: visible
				.filter((entry) => !entry.exact)
				.map((entry) => entry.command),
		}
	}, [atRoot, commandScoped, lookup, hasAnyRole])

	const runCommand = React.useCallback(
		async (command: PaletteCommand) => {
			try {
				if (command.group === "results") {
					pushRecent(browserStorage(), {
						id: command.id,
						kind: "entity",
						label: command.label,
						description: command.description,
						query: query.trim(),
					})
				} else if (command.group !== "page" && command.group !== "recent") {
					pushRecent(browserStorage(), {
						id: command.id,
						kind: "command",
						label: command.label,
						description: command.description,
					})
				}
				await command.perform(ctx)
			} catch (error) {
				toast.error(error instanceof Error ? error.message : "Command failed")
			}
		},
		[ctx, query],
	)

	const recentCommands = React.useMemo(() => {
		if (!atRoot || query.trim() !== "") return []
		return recents.map(
			(entry): PaletteCommand => ({
				id: `recent:${entry.id}`,
				label: entry.label,
				description: entry.description,
				icon: History,
				group: "recent",
				perform: () => {
					if (entry.kind === "entity" && entry.query) {
						setQuery(entry.query)
						return
					}
					const command = staticCommands.find((c) => c.id === entry.id)
					if (command) {
						void runCommand(command)
					}
				},
			}),
		)
	}, [atRoot, query, recents, staticCommands, runCommand])

	const visibleGroups = React.useMemo(() => {
		if (!atRoot) {
			const top = steps[steps.length - 1]
			return [
				{
					key: "step" as const,
					label: top?.title ?? "",
					commands: rankCommands(
						query,
						filterByRoles(top?.commands ?? [], hasAnyRole),
					),
				},
			]
		}

		const emptyQuery = effectiveQuery === ""
		const groups: {
			key: PaletteGroup | "step" | "match"
			label: string
			commands: PaletteCommand[]
		}[] = []

		const pageCommands = rankCommands(
			effectiveQuery,
			filterByRoles(contextualCommands, hasAnyRole),
		)
		if (pageCommands.length > 0) {
			groups.push({
				key: "page",
				label: PALETTE_GROUP_LABELS.page,
				commands: pageCommands,
			})
		}

		if (recentCommands.length > 0) {
			groups.push({
				key: "recent",
				label: PALETTE_GROUP_LABELS.recent,
				commands: recentCommands,
			})
		}

		if (entityCommands.exact.length > 0) {
			groups.push({
				key: "match",
				label: "Exact match",
				commands: entityCommands.exact,
			})
		}

		const navigatePool = emptyQuery
			? navigationCommands
			: [...navigationCommands, ...presetCommands]
		groups.push({
			key: "navigate",
			label: PALETTE_GROUP_LABELS.navigate,
			commands: rankCommands(
				effectiveQuery,
				filterByRoles(navigatePool, hasAnyRole),
			),
		})

		for (const [key, pool] of [
			["create", createCommands],
			["run", runCommands],
			["switch", switchCommands],
		] as const) {
			groups.push({
				key,
				label: PALETTE_GROUP_LABELS[key],
				commands: rankCommands(effectiveQuery, filterByRoles(pool, hasAnyRole)),
			})
		}

		groups.push({
			key: "results",
			label: PALETTE_GROUP_LABELS.results,
			commands: entityCommands.partial,
		})

		return groups.filter((group) => group.commands.length > 0)
	}, [
		atRoot,
		steps,
		query,
		effectiveQuery,
		contextualCommands,
		recentCommands,
		entityCommands,
		navigationCommands,
		presetCommands,
		createCommands,
		runCommands,
		switchCommands,
		hasAnyRole,
	])

	const commandsById = React.useMemo(() => {
		const map = new Map<string, PaletteCommand>()
		for (const group of visibleGroups) {
			for (const command of group.commands) {
				map.set(command.id, command)
			}
		}
		return map
	}, [visibleGroups])

	const openSubmenu = React.useCallback(
		async (command: PaletteCommand) => {
			if (!command.submenu) return
			try {
				const step = await command.submenu(ctx)
				push(step)
			} catch {
				toast.error("Failed to open submenu")
			}
		},
		[ctx, push],
	)

	const onKeyDown = React.useCallback(
		(event: React.KeyboardEvent) => {
			if (event.key === "Backspace" && query === "" && steps.length > 0) {
				event.preventDefault()
				pop()
				return
			}
			const wantsSubmenu =
				(event.key === "ArrowRight" && query === "") ||
				(event.key === "Enter" && (event.metaKey || event.ctrlKey))
			if (wantsSubmenu) {
				const command = commandsById.get(value)
				if (command?.submenu) {
					event.preventDefault()
					void openSubmenu(command)
				}
			}
		},
		[query, steps.length, pop, commandsById, value, openSubmenu],
	)

	const isLoading = lookup.isLoading || stepLoading
	const kindLabel =
		atRoot && !commandScoped && effectiveQuery.length >= 2
			? KIND_LABELS[lookup.classified.kind]
			: undefined
	const topStep = steps[steps.length - 1]

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent
				className="overflow-hidden p-0 sm:max-w-2xl"
				showCloseButton={false}
				onEscapeKeyDown={(event) => {
					if (steps.length > 0) {
						event.preventDefault()
						pop()
					}
				}}
			>
				<DialogHeader className="sr-only">
					<DialogTitle>Command palette</DialogTitle>
					<DialogDescription>
						Search, navigate, and run admin commands
					</DialogDescription>
				</DialogHeader>
				<Command
					shouldFilter={false}
					value={value}
					onValueChange={setValue}
					onKeyDown={onKeyDown}
					loop
					className="[&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-input]]:h-12"
				>
					{steps.length > 0 && (
						<div className="flex items-center gap-1 border-b px-3 py-1.5 text-xs text-muted-foreground">
							{steps.map((step, index) => (
								<React.Fragment key={`${step.title}-${index}`}>
									{index > 0 && <ChevronRight className="size-3" />}
									<span>{step.title}</span>
								</React.Fragment>
							))}
						</div>
					)}
					<CommandInput
						value={query}
						onValueChange={setQuery}
						placeholder={
							topStep
								? (topStep.placeholder ?? "Filter…")
								: "Search or paste anything…  (@ user · > commands)"
						}
					/>
					{kindLabel && (
						<div className="border-b px-3 py-1 text-xs text-muted-foreground">
							Detected: {kindLabel}
						</div>
					)}
					<CommandList className="max-h-[420px]">
						{isLoading && <CommandLoading>Searching…</CommandLoading>}
						{!isLoading && <CommandEmpty>No results.</CommandEmpty>}
						{visibleGroups.map((group, index) => (
							<React.Fragment key={group.key}>
								{index > 0 && <CommandSeparator />}
								<CommandGroup heading={group.label}>
									{group.commands.map((command) => (
										<CommandItem
											key={command.id}
											value={command.id}
											onSelect={() => void runCommand(command)}
										>
											{command.icon && (
												<command.icon className="size-4 shrink-0" />
											)}
											<div className="flex min-w-0 flex-col">
												<span className="truncate">{command.label}</span>
												{command.description && (
													<span className="truncate text-xs text-muted-foreground">
														{command.description}
													</span>
												)}
											</div>
											{command.shortcutHint && (
												<CommandShortcut>
													{command.shortcutHint}
												</CommandShortcut>
											)}
											{command.submenu && (
												<ChevronRight className="ml-auto size-4 text-muted-foreground" />
											)}
										</CommandItem>
									))}
								</CommandGroup>
							</React.Fragment>
						))}
					</CommandList>
				</Command>
			</DialogContent>
		</Dialog>
	)
}
